import React, { useState, useEffect, useRef } from "react";
import {
  BookOpen,
  Scale,
  FileText,
  ArrowRight,
  X,
  Loader2,
} from "lucide-react";
import Footer from "@/components/Footer";
import HeaderPage from "@/components/HeaderPages";

// --- TYPE DEFINITIONS ---
interface ResourceItem {
  id: number;
  icon: React.ElementType;
  title: string;
  description: string;
  action: "link" | "modal";
  actionText: string;
}

// --- MOCK DATA BASED ON PDFS AND SEARCH RESULTS ---

// Snippet of the Rwanda Land Reference Prices 2021 document's preamble
const MOCK_LAND_REFERENCE_SNIPPET = `
This document, published as Official Gazette n° Special of 01/12/2021, contains the 'IBICIRO FATIZO BY'UBUTAKA MU RWANDA 2021' (RWANDA LAND REFERENCE PRICES 2021). 

It was prepared by The Institute of Real Property Valuers in Rwanda (IRPV) to serve as a guidance tool for professional valuers and the general public. These prices reflect the value of the land element of the property without any development (buildings, crops, etc.). 

Valuers are required to use these reference prices as a baseline, but they do not replace the necessity for a certified professional valuer to determine the final fair market value of a property using appropriate valuation methodologies. The prices are categorized based on administrative sectors (Province, District, Sector, Cell, Umudugudu) and also by designated land use (especially in Kigali and satellite cities).
`;

// --- COMPONENTS ---

// 1. Modal for displaying PDF content (simulated viewer)
interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4 transition-opacity duration-300">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col transition-transform duration-300 scale-100">
        <header className="flex justify-between items-center p-5 border-b border-gray-200 bg-indigo-50 rounded-t-xl">
          <h2 className="text-xl font-bold text-indigo-700 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-indigo-100 text-gray-500 hover:text-indigo-700 transition"
            aria-label="Close viewer"
          >
            <X className="w-6 h-6" />
          </button>
        </header>

        <main className="p-6 overflow-y-auto flex-grow bg-gray-50">
          <div className="bg-white p-6 rounded-lg shadow-inner border border-gray-100 font-serif text-gray-700 leading-relaxed">
            <p className="text-sm font-semibold text-red-600 mb-4 bg-red-50 p-3 rounded">
              *Note: This content is a simulated snippet based on the 'RWANDA
              LAND REFERENCE PRICES 2021.pdf' document you provided. For the
              full, official gazette, please refer to the IRPV or NLA official
              websites.
            </p>
            <h3 className="text-lg font-bold mb-3 text-gray-800">
              Document Summary & Purpose
            </h3>
            <pre className="whitespace-pre-wrap p-4 bg-gray-100 border rounded-lg text-sm">
              {content}
            </pre>

            <div className="mt-6">
              <h3 className="text-lg font-bold mb-3 text-gray-800">
                Sample Data Structure (Simulated Table)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                  <thead className="bg-indigo-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">
                        District
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">
                        Sector
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">
                        Residential Price (RWF/sqm)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">
                        Commercial Price (RWF/sqm)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-sm">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">Gasabo</td>
                      <td className="px-6 py-4 whitespace-nowrap">Remera</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        15,000 - 60,000
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        50,000 - 150,000
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        Nyarugenge
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">Muhima</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        12,000 - 45,000
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        40,000 - 100,000
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">Kicukiro</td>
                      <td className="px-6 py-4 whitespace-nowrap">Gikondo</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        10,000 - 35,000
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        30,000 - 80,000
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// 2. Individual Resource Card
interface ResourceCardProps extends ResourceItem {
  onClick: () => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  icon: Icon,
  title,
  description,
  actionText,
  onClick,
}) => (
  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 flex flex-col border border-gray-100">
    <Icon className="w-8 h-8 text-indigo-500 mb-3" />
    <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4 flex-grow text-sm">{description}</p>
    <button
      onClick={onClick}
      className="mt-4 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-300 shadow-md hover:shadow-lg"
    >
      {actionText} <ArrowRight className="w-4 h-4 ml-2" />
    </button>
  </div>
);

// 3. Main App Component
const Resources: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate data loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // Simulate network delay
    return () => clearTimeout(timer);
  }, []);

  const resources: ResourceItem[] = [
    {
      id: 1,
      icon: FileText,
      title: "Rwanda Land Reference Prices 2021",
      description:
        "Access the official gazette containing the standard reference prices for land across various sectors and land uses in Rwanda.",
      action: "modal",
      actionText: "View Online Document",
    },
    {
      id: 2,
      icon: BookOpen,
      title: "IRPV Valuation Standards & Law",
      description:
        "Review the official Law N° 17/2010 establishing and organizing the real property valuation profession in Rwanda (Source 1.6).",
      action: "link",
      actionText: "Read Legal Framework",
    },
    {
      id: 3,
      icon: Scale,
      title: "Current Market Trends (2025)",
      description:
        "Explore the latest insights on Rwanda’s real estate market, focusing on urbanization, affordable housing, and investment projections (Source 3.1).",
      action: "link",
      actionText: "View Market Analysis",
    },
  ];

  const handleResourceClick = (resource: ResourceItem) => {
    if (resource.action === "modal") {
      setIsModalOpen(true);
    } else if (resource.action === "link") {
      // In a real application, this would navigate or open an external link
      console.log(`Navigating to: ${resource.title}`);
      // NOTE: Using a custom modal/notification instead of alert()
      // For this example, we'll keep the console log and a simple notification strategy for demonstration
      alert(
        `Simulated Action: Opening resource for "${resource.title}". In a live app, this would open the specific legal/market document.`
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-4" />
          <p className="text-lg text-gray-600 font-medium">
            Loading Resource Data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderPage />
    <div className="min-h-screen bg-gray-50">
      <div className=" font-sans p-4 sm:p-8">
        <header className="mb-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-2 tracking-tight">
            Property Valuation Resources
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Essential documentation, legal framework, and current market context
            for professional property valuation in Rwanda.
          </p>
        </header>

        {/* Resource Cards Section */}
        <section className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-2">
            Key Documents & Frameworks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource) => (
              <ResourceCard
                key={resource.id}
                {...resource}
                onClick={() => handleResourceClick(resource)}
              />
            ))}
          </div>
        </section>

        {/* PDF Viewer Modal */}
        <PdfViewerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="RWANDA LAND REFERENCE PRICES 2021 (Official Gazette)"
          content={MOCK_LAND_REFERENCE_SNIPPET}
        />
      </div>
     
    </div>
       <Footer />
    </div>
  );
};

export default Resources;
